
import Vue from "vue";
import { Alert, Vehicle } from "@/commons/types";
import Alerter from "@/components/Alerter.vue";
import { latLng } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import { DeviceGpsSchema, DeviceDataService } from "@/commons/client";
import { format } from "date-fns";
import * as L from "leaflet";

// Funzione utility per generare stringa ISO UTC con .000Z da data e ora locale
function localToUtcIso(dateStr: string, timeStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);
  // Crea la data locale
  const localDate = new Date(year, month - 1, day, hour, minute, 0);
  // ISO sempre UTC, rimpiazza ms con .000Z per richiesta backend
  return localDate.toISOString().replace(/\.\d{3}Z$/, ".000Z");
}

export default Vue.extend({
  name: "Map",

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    Alerter,
  },

  data: () => {
    // Funzione helper per ottenere la data locale formattata
    const getLocalDateString = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      startDateOpen: false,
      startTimeOpen: false,
      endDateOpen: false,
      endTimeOpen: false,
      vehicleId: "",
      selectedVehicle: null,
      isLoading: false,
      hasSearched: false,
      dateFilters: {
        from: getLocalDateString(),
        fromTime: "00:00",
        to: getLocalDateString(),
        toTime: "23:59",
      },
      map: {
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
          '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        center: {
          lat: 44.10245,
          lng: 9.824083,
        },
        zoom: 13,
      },
      marker: {
        draggable: true,
        lat: 44.10245,
        lng: 9.824083,
      },
      points: [] as DeviceGpsSchema[],
      alerts: [] as Alert[],
      vehicleColors: {} as Record<string, string>,
    };
  },

  computed: {
    rules() {
      return this.$store.state.genericRules;
    },
    organizationId() {
      return this.$store.state.organizationId;
    },
    vehiclesList() {
      const list = this.$store.state.vehicles.map((v: Vehicle) => ({
        value: v.vehicle_id,
        label: `${v.plate} - ${v.vehicle_id}`,
      }));
      // Se l'utente NON ha accesso limitato, mostra "ALL", altrimenti no.
      if (!this.hasLimitedDataAccess) {
        return [{ value: "ALL", label: "Tutti" }, ...list];
      }
      return list;
    },
    maxCardWidth(): string {
      /*************************************
       * Return tthe right card length based on
       * current breakpoint.
       *************************************/
      // switch (this.$vuetify.breakpoint.name) {
      //   case 'xs' || 'sm': return 400;
      // } return 550;
      return "100%";
    },
    startDatetime(): string {
      /*************************************
       * Return the start datetime in UTC format
       * Interpreta la data/ora locale come UTC per il server
       *************************************/
      // return `${this.dateFilters.from}T${this.dateFilters.fromTime}:00.000Z`;
      return localToUtcIso(this.dateFilters.from, this.dateFilters.fromTime);
    },
    endDatetime(): string {
      /*************************************
       * Return the end datetime in UTC format
       * Interpreta la data/ora locale come UTC per il server
       *************************************/
      // return `${this.dateFilters.to}T${this.dateFilters.toTime}:00.000Z`;
      return localToUtcIso(this.dateFilters.to, this.dateFilters.toTime);
    },
    hasValueStart(): boolean {
      /*************************************
       * Return true if the start datetime
       * is set.
       *************************************/
      return this.dateFilters.from !== "" && this.dateFilters.fromTime !== "";
    },
    hasValueEnd(): boolean {
      /*************************************
       * Return true if the end datetime
       * is set.
       *************************************/
      return this.dateFilters.to !== "" && this.dateFilters.toTime !== "";
    },
    hasValueStartOrEnd(): boolean {
      /*************************************
       * Return true if the start or end
       * datetime is set.
       *************************************/
      return this.hasValueStart || this.hasValueEnd;
    },
    endpoint(): string {
      /*************************************
       * Return the endpoint to use
       *************************************/
      let organizationId = this.$store.state.organizationId;
      let baseEndpoint = `${organizationId}/vehicles/${
        this.vehicleId
      }/data/gps/${this.hasValueStartOrEnd ? "?" : ""}`;
      if (this.hasValueStart) {
        baseEndpoint += `start=${this.startDatetime}${
          this.hasValueEnd ? "&" : ""
        }`;
      }
      if (this.hasValueEnd) {
        baseEndpoint += `end=${this.endDatetime}`;
      }
      return baseEndpoint;
    },
    isValidForm(): boolean {
      /*************************************
       * Return true if the form is valid
       *************************************/
      return (
        this.vehicleId !== "" &&
        this.vehicleId !== undefined &&
        this.hasValueStartOrEnd
      );
    },
    hasLimitedDataAccess(): boolean {
      /*************************************
       * Return true if the user has limited
       * data access
       *************************************/
      return this.$store.state.user.has_limited_data_access;
    },
  },

  watch: {
    selectedVehicle(newVal) {
      if (newVal && newVal !== this.vehicleId) {
        this.vehicleId = newVal;
        this.getPoints();
      }
    },
  },

  methods: {
    latLng: latLng,
    async getGpsData(): Promise<DeviceGpsSchema[]> {
      if (!this.isValidForm && this.selectedVehicle !== "ALL") {
        this.alerts.push({
          type: "error",
          text: "Compilare il form per vedere i dati",
        });
        return [];
      }
      let start: string | undefined;
      let stop: string | undefined;
      if (this.hasLimitedDataAccess) {
        const now = new Date();
        stop = now.toISOString().replace(/\.\d{3}Z$/, ".000Z");
        const tenMinAgo = new Date(now.getTime() - 10 * 60 * 1000);
        start = tenMinAgo.toISOString().replace(/\.\d{3}Z$/, ".000Z");
      } else {
        start = this.startDatetime;
        stop = this.endDatetime;
      }
      try {
        if (this.selectedVehicle === "ALL") {
          return await DeviceDataService.organizationVehiclesLatestGpsV1OrganizationsPkVehiclesDataGpsGet(
            this.organizationId,
            start,
            stop
          );
        } else {
          return await DeviceDataService.organizationVehiclesDataGpsV1OrganizationsPkVehiclesVehicleIdDataGpsGet(
            this.organizationId,
            this.vehicleId,
            start,
            stop,
            100
          );
        }
      } catch (error) {
        console.error("Errore nel recupero dati GPS:", error);
        this.alerts.push({
          type: "error",
          text: "Errore nel recupero dei dati",
        });
        return [];
      }
    },
    async getPoints(): Promise<void> {
      this.isLoading = true;
      this.hasSearched = true;
      try {
        this.points = await this.getGpsData();
        this.alerts.push({
          type: "success",
          text: `Trovati ${this.points.length} punti mappa.`,
        });
        if (this.points.length > 0) {
          this.setMapCenter();
        }
      } catch (error) {
        console.error("Errore nel caricamento punti:", error);
      } finally {
        this.isLoading = false;
      }
    },
    setMapCenter(): void {
      if (this.points.length > 0) {
        this.map.center = {
          lat: this.points[0].lat / 100000,
          lng: this.points[0].lon / 100000,
        };
      }
    },
    initFormFromQueryString(): void {
      this.vehicleId =
        this.$route.query.vehicleId &&
        typeof this.$route.query.vehicleId == "string"
          ? this.$route.query.vehicleId
          : "";
      const getLocalDateString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      this.dateFilters.from =
        this.$route.query.from && typeof this.$route.query.from == "string"
          ? this.$route.query.from
          : getLocalDateString();
      this.dateFilters.to =
        this.$route.query.to && typeof this.$route.query.to == "string"
          ? this.$route.query.to
          : getLocalDateString();
    },
    formatTimestamp(timestamp?: string): string {
      if (!timestamp) return "-";
      // Verifica se ha una timezone
      const hasTimezone =
        timestamp.includes("Z") ||
        timestamp.includes("+") ||
        /T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}/.test(timestamp);
      let date;
      if (hasTimezone) {
        date = new Date(timestamp);
      } else {
        // Tratta le date senza Z come locali
        const m = timestamp.match(
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/
        );
        if (m) {
          date = new Date(
            Number(m[1]),
            Number(m[2]) - 1,
            Number(m[3]),
            Number(m[4]),
            Number(m[5]),
            Number(m[6])
          );
        } else {
          date = new Date(timestamp);
        }
      }
      if (isNaN(date.getTime())) {
        console.warn(`Data invalida nel formatTimestamp: ${timestamp}`);
        return "-";
      }
      return date.toLocaleString("it-IT", {
        timeZone: "Europe/Rome",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getVehicleColor(vehicleId: string): string {
      if (this.vehicleColors[vehicleId]) return this.vehicleColors[vehicleId];
      let hash = 0;
      for (let i = 0; i < vehicleId.length; i++) {
        hash = vehicleId.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360;
      const color = `hsl(${hue}, 70%, 50%)`;
      this.vehicleColors[vehicleId] = color;
      return color;
    },
    getIcon(vehicleId?: string) {
      const color = this.getVehicleColor(vehicleId || "unknown");
      return L.divIcon({
        className: "custom-div-icon",
        html: `<div class="pin" style="--pin-color:${color};"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [0, -20],
      });
    },
  },

  created() {
    this.initFormFromQueryString();
    // Rimuovi il $nextTick che sovrascrive le date
    // Le date sono già inizializzate correttamente nel data()
  },
});
